import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PeopleIcon from '@mui/icons-material/People'
import CampaignIcon from '@mui/icons-material/Campaign'
import SendIcon from '@mui/icons-material/Send'
import AddIcon from '@mui/icons-material/Add'
import FolderIcon from '@mui/icons-material/Folder'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts'
import { API_URL } from '../../services/settings'
import { leadStatusChoices } from '../leads/constants'

const StatCard = ({ title, value, icon: Icon, subtitle, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {Icon && <Icon sx={{ fontSize: 40, color: `${color}.light`, opacity: 0.5, ml: 2 }} />}
      </Box>
    </CardContent>
  </Card>
)

const ProgressCard = ({ title, current, target, unit = '' }) => {
  const percentage = Math.min((current / target) * 100, 100)
  const isGoalMet = current >= target

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip
            label={`${current}/${target}${unit}`}
            color={isGoalMet ? 'success' : 'default'}
            size="small"
            icon={isGoalMet ? <TrendingUpIcon /> : undefined}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: 'action.disabled',
            '& .MuiLinearProgress-bar': {
              backgroundColor: isGoalMet ? 'success.main' : 'primary.main',
              borderRadius: 1,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {Math.round(percentage)}% of target
          </Typography>
          <Typography variant="caption" color={isGoalMet ? 'success.main' : 'text.secondary'}>
            {isGoalMet ? '✓ Goal met!' : `${target - current} to go`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

const SalesDashboard = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [leads, setLeads] = useState([])
  const [campaignStats, setCampaignStats] = useState(null)
  const [leadDirectories, setLeadDirectories] = useState([])
  const [loading, setLoading] = useState(true)
  const [campaigns, setCampaigns] = useState([])
  const [openCreateDialog, setOpenCreateDialog] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [formData, setFormData] = useState({
    website: '',
    email: '',
    firstName: '',
    lastName: '',
    campaignId: '',
    analysis: '',
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token')

        // Fetch lead stats
        const statsResponse = await fetch(`${API_URL}/leads/dashboard/sales`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (statsResponse.ok) {
          const data = await statsResponse.json()
          setStats(data.data)
        }

        // Fetch campaigns statistics
        const campaignsResponse = await fetch(`${API_URL}/campaigns?page=1&page_size=999&sort=id`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (campaignsResponse.ok) {
          const data = await campaignsResponse.json()
          const campaigns = data.results || []

          // Calculate campaign stats
          const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE').length
          let pendingLeads = 0
          let sentLeads = 0

          // Get detailed lead stats for each campaign
          for (const campaign of campaigns) {
            const leadsResponse = await fetch(`${API_URL}/campaigns/${campaign.id}/leads?page=1&page_size=999`, {
              headers: { 'Authorization': `Bearer ${token}` },
            })
            if (leadsResponse.ok) {
              const leadsData = await leadsResponse.json()
              const campaignLeads = leadsData.results || []
              pendingLeads += campaignLeads.filter(l => l.status === 'PENDING').length
              sentLeads += campaignLeads.filter(l => l.status === 'SENT').length
            }
          }

          // Create chart data showing campaigns distribution
          const campaignChartData = campaigns.map(c => ({
            name: c.name,
            value: c.leads_count || 0,
          })).filter(c => c.value > 0)

          setCampaigns(campaigns)
          setCampaignStats({
            activeCampaigns,
            pendingLeads,
            sentLeads,
            totalCampaigns: campaigns.length,
            chartData: campaignChartData,
          })
        }

        // Fetch lead directories
        const directoriesResponse = await fetch(`${API_URL}/dashboards/leads`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (directoriesResponse.ok) {
          const data = await directoriesResponse.json()
          setLeadDirectories(data.data?.recent_directories || [])
        }

        // Fetch latest leads
        const leadsResponse = await fetch(`${API_URL}/leads?page=1&page_size=10&order=DESC&sort=created_at`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (leadsResponse.ok) {
          const data = await leadsResponse.json()
          const leadsData = data.results || []

          // Fetch campaigns for each lead
          const leadsWithCampaigns = await Promise.all(
            leadsData.map(async (lead) => {
              try {
                const campaignsResponse = await fetch(`${API_URL}/leads/${lead.id}/campaigns`, {
                  headers: { 'Authorization': `Bearer ${token}` },
                })
                if (campaignsResponse.ok) {
                  const campaignsData = await campaignsResponse.json()
                  const campaigns = campaignsData.data || []
                  // Get last campaign (most recent)
                  const lastCampaign = campaigns.length > 0 ? campaigns[campaigns.length - 1] : null
                  return {
                    ...lead,
                    lastCampaign,
                  }
                }
              } catch (e) {
                console.error('Error fetching campaigns:', e)
              }
              return lead
            })
          )

          setLeads(leadsWithCampaigns)
        }
      } catch (e) {
        console.error('Error fetching dashboard data:', e)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleCreateLead = async () => {
    if (!formData.website) {
      alert('Please fill in website')
      return
    }

    setCreateLoading(true)
    try {
      const token = localStorage.getItem('token')

      // Extract domain from URL for fallback
      let domain = formData.website
      if (!domain.startsWith('http')) {
        domain = `https://${domain}`
      }
      const url = new URL(domain)
      const hostname = url.hostname.replace('www.', '')

      // Use provided values or fallback to domain-based defaults
      const firstName = formData.firstName.trim() || hostname
      const lastName = formData.lastName.trim() || 'Quick Add'
      const email = formData.email.trim() || `contact@${hostname}`

      // Create lead
      const leadResponse = await fetch(`${API_URL}/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: email,
          website: formData.website,
        }),
      })

      if (!leadResponse.ok) {
        throw new Error('Failed to create lead')
      }

      const leadData = await leadResponse.json()
      const leadId = leadData.id

      // Add note if analysis provided
      if (formData.analysis) {
        await fetch(`${API_URL}/leads/${leadId}/notes`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: formData.analysis,
          }),
        })
      }

      // Add lead to campaign
      if (formData.campaignId) {
        await fetch(`${API_URL}/campaigns/${formData.campaignId}/leads`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: leadId,
          }),
        })
      }

      // Reset form and close dialog
      setFormData({ website: '', campaignId: '', analysis: '' })
      setOpenCreateDialog(false)
      alert('Lead created and added to campaign!')

      // Refresh data
      window.location.reload()
    } catch (e) {
      console.error('Error creating lead:', e)
      alert('Error: ' + e.message)
    } finally {
      setCreateLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load dashboard</Typography>
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Sales Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track leads, targets, and team performance
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
          sx={{ mb: 2 }}
        >
          Quick Add Lead
        </Button>
      </Box>

      {/* Quick Add Lead Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Add Lead</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Website URL"
              placeholder="example.com or https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              placeholder="contact@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="First Name"
                placeholder="John (optional)"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                placeholder="Doe (optional)"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={formData.campaignId}
                label="Campaign"
                onChange={(e) => setFormData({ ...formData, campaignId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Analysis / Notes"
              placeholder="e.g., Site à refaire, avocat"
              value={formData.analysis}
              onChange={(e) => setFormData({ ...formData, analysis: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLead}
            variant="contained"
            disabled={createLoading}
          >
            {createLoading ? 'Creating...' : 'Create & Add to Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Directories */}
      {leadDirectories && leadDirectories.length > 0 && (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Lead Directories (Quick Access)
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/lead_directories')}
                sx={{ textTransform: 'none' }}
              >
                See All Directories
              </Button>
            </Box>
            <Grid container spacing={2}>
              {leadDirectories.map((directory) => (
                <Grid item xs={12} sm={6} md={4} key={directory.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 2 },
                      transition: 'boxShadow 0.3s'
                    }}
                    onClick={() => navigate(`/lead_directories/${directory.id}/show`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <FolderIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {directory.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              display: 'block',
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            component="a"
                            href={directory.site}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={directory.site}
                          >
                            {directory.site.replace(/^https?:\/\/(www\.)?/, '').substring(0, 40)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {directory.industry && (
                              <Chip
                                label={directory.industry}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {directory.type && (
                              <Chip
                                label={directory.type}
                                size="small"
                                variant="filled"
                                color="primary"
                              />
                            )}
                          </Box>
                          {directory.estimated_leads_count && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Est. {directory.estimated_leads_count} leads
                            </Typography>
                          )}
                        </Box>
                        <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Goals & Progress */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Weekly Goal"
            current={stats.week_leads}
            target={stats.week_target}
            unit=" leads"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Monthly Goal"
            current={stats.month_leads}
            target={stats.month_target}
            unit=" leads"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                My Contribution This Month
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats.user_month_leads || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                This week: {stats.user_week_leads || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Leads"
            value={stats.total_leads}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Leads"
            value={stats.status_stats.new}
            subtitle="Waiting to be contacted"
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Converted"
            value={stats.status_stats.converted}
            subtitle="Successfully converted"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Lost"
            value={stats.status_stats.lost}
            subtitle="No longer pursuing"
            color="error"
          />
        </Grid>
      </Grid>

      {/* Campaign Metrics */}
      {campaignStats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Campaigns"
              value={campaignStats.activeCampaigns}
              icon={CampaignIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Leads"
              value={campaignStats.pendingLeads}
              subtitle="Awaiting email send"
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Sent Emails"
              value={campaignStats.sentLeads}
              subtitle="Successfully sent"
              icon={SendIcon}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Campaigns"
              value={campaignStats.totalCampaigns}
              subtitle="All campaigns"
              color="info"
            />
          </Grid>
        </Grid>
      )}

      {/* Campaigns Distribution Chart */}
      {campaignStats && campaignStats.chartData && campaignStats.chartData.length > 0 && (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Leads Distribution by Campaign
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300 }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={campaignStats.chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {campaignStats.chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={[
                          '#3b82f6',
                          '#ef4444',
                          '#10b981',
                          '#f59e0b',
                          '#8b5cf6',
                          '#ec4899',
                          '#06b6d4',
                          '#6366f1',
                        ][index % 8]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} leads`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Status Breakdown */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Lead Status Breakdown
          </Typography>
          <Grid container spacing={1}>
            {leadStatusChoices.map(({ id: status }) => {
              const count = stats.status_stats[status]
              return (
              <Grid item xs={12} sm={6} md={3} key={status}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {status.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {count}
                  </Typography>
                </Box>
              </Grid>
            )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Team Performance - This Month */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Team Performance - This Month
          </Typography>
          {stats.month_by_user && stats.month_by_user.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Leads Added</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.month_by_user
                    .sort((a, b) => b.leads_count - a.leads_count)
                    .map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>{user.user_name}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={user.leads_count}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
              No leads added this month yet.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Latest Leads */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Latest 10 Leads
          </Typography>
          {leads.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Website</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      hover
                      onClick={() => navigate(`/leads/${lead.id}/show`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>
                        {lead.website ? (
                          <a
                            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lead.website}
                          </a>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{lead.company || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={lead.status}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell variant="caption">
                        {lead.created_by ? lead.created_by.fullname : '-'}
                      </TableCell>
                      <TableCell>
                        {lead.lastCampaign ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {lead.lastCampaign.name}
                            </Typography>
                            <Chip
                              label={lead.lastCampaign.lead_status}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell variant="caption">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
              No leads yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  )
}

export default SalesDashboard
